import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { cloneDeep } from 'lodash';
import Decimal from 'big.js';
import FormFieldInterface from '@vertical-plus/vue-js-components/lib/model/vos/form/form-field-interface';
import FormFieldSetInterface from '@vertical-plus/vue-js-components/lib/model/vos/form/form-field-set-interface';
import FormInterface from '@vertical-plus/vue-js-components/lib/model/vos/form/form-interface';
import Invoice from '@/model/entity/billing/invoice';
import InvoiceDto from '@/model/entity/billing/invoice-dto';
import InvoiceService from '@/model/service/billing/invoice-service';

@Component
export default class InvoiceForm extends Vue
{
    @Prop()
    private invoice!: Invoice;

    @Prop({
        type: Boolean,
        default: false,
    })
    private saving!: boolean;

    private form: FormInterface|null = null;
    private clientId: number|null = null;

    private created()
    {
        this.generateFormFromInvoice();

        // Defined client
        if (this.$route.query.client && Number.isInteger(parseInt(this.$route.query.client as string)))
        {
            this.clientId = parseInt(this.$route.query.client as string);
        }
    }

    @Watch('invoice')
    private onInvoiceChanged()
    {
        this.generateFormFromInvoice();
    }

    /**
     * Handles form saves
     *
     * @param form
     */

    private async onSave(form: FormInterface)
    {
        if (!this.invoice)
        {
            return;
        }

        // Generate a invoice from the form data
        const invoice = this.generateInvoiceFromForm(form);

        // Emit a save event with the invoice
        this.$emit('save', invoice);
    }

    /**
     * Generates the form from the invoice
     */

    private generateFormFromInvoice()
    {
        if (!this.invoice)
        {
            this.form = null;
            return;
        }

        const form: FormInterface =
        {
            items: [
                {
                    title: this.$t('general.field.basic_details').toString(),
                    fields: [
                        {
                            key: 'techspec',
                            type: 'integer',
                            title: this.$t('general.field.techspec').toString(),
                            value: this.invoice.techspecId,
                            required: false,
                            clearable: true,
                            optionsCallback: async(search: string) => this.$serviceContainer.techspecService.techspecFormOptionsCallback(
                                search,
                                this.clientId,
                                true,
                            ),
                            titlesCallback: async(values: string[]) => this.$serviceContainer.techspecService.techspecFormTitlesCallback(values),
                            disableInitialOptionsCallback: true,
                            disableEmptySearchResults: true,
                        },
                        {
                            key: 'service',
                            type: 'integer',
                            title: this.$t('invoice.field.service').toString(),
                            value: this.invoice.serviceId,
                            required: false,
                            clearable: true,
                            optionsCallback: async(search: string) => this.$serviceContainer.serviceService.serviceFormOptionsCallback(search, this.clientId),
                            titlesCallback: async(values: string[]) => this.$serviceContainer.serviceService.serviceFormTitlesCallback(values),
                            disableInitialOptionsCallback: true,
                            disableEmptySearchResults: true,
                        },
                        {
                            key: 'domain',
                            type: 'integer',
                            title: this.$t('invoice.field.domain').toString(),
                            value: this.invoice.domainId,
                            required: false,
                            clearable: true,
                            optionsCallback: async(search: string) => this.$serviceContainer.domainService.domainFormOptionsCallback(search, this.clientId),
                            titlesCallback: async(values: string[]) => this.$serviceContainer.domainService.domainFormTitlesCallback(values),
                            disableInitialOptionsCallback: true,
                            disableEmptySearchResults: true,
                        },
                        {
                            key: 'invoiceNumber',
                            type: 'string',
                            title: this.$t('invoice.field.number').toString(),
                            value: this.invoice.invoiceNumber,
                            required: false,
                        },
                        {
                            key: 'jobCode',
                            type: 'integer',
                            title: this.$t('job.field.code').toString(),
                            value: this.invoice.jobCodeId,
                            required: false,
                            clearable: true,
                            optionsCallback: async(search: string) => this.$serviceContainer.jobService.jobCodeFormOptionsCallback(search),
                            titlesCallback: async(values: string[]) => this.$serviceContainer.jobService.jobCodeFormTitlesCallback(values),
                        },
                        {
                            key: 'paymentTerms',
                            type: 'string',
                            title: this.$t('invoice.field.payment_terms').toString(),
                            value: this.invoice.paymentTerms,
                            required: false,
                            options: InvoiceService.paymentTermsOptions,
                            dropdown: true,
                        },
                        {
                            key: 'title',
                            type: 'string',
                            title: this.$t('general.field.title').toString(),
                            value: this.invoice.title,
                            required: true,
                        },
                        {
                            key: 'notes',
                            type: 'string',
                            title: this.$t('general.field.notes').toString(),
                            value: this.invoice.notes,
                            required: false,
                            multiline: true,
                            rich: true,
                            markdown: true,
                        },
                        {
                            key: 'amount',
                            type: 'decimal',
                            title: this.$t('invoice.field.amount').toString(),
                            value: this.invoice.amount.toString(),
                            required: true,
                        },
                        {
                            key: 'adwordsAmount',
                            type: 'decimal',
                            title: this.$t('invoice.field.adwords_amount').toString(),
                            value: this.invoice.adwordsAmount.toString(),
                            required: true,
                        },
                        {
                            key: 'miscAmount',
                            type: 'decimal',
                            title: this.$t('invoice.field.misc_amount').toString(),
                            value: this.invoice.miscAmount.toString(),
                            required: true,
                        },
                        {
                            key: 'dateRequested',
                            type: 'date',
                            title: this.$t('invoice.field.date_requested').toString(),
                            value: this.invoice.dateRequested,
                            required: true,
                        },
                        {
                            key: 'dateSent',
                            type: 'date',
                            title: this.$t('invoice.field.date_sent').toString(),
                            value: this.invoice.dateSent,
                            required: false,
                        },
                        {
                            key: 'datePaid',
                            type: 'date',
                            title: this.$t('invoice.field.date_paid').toString(),
                            value: this.invoice.datePaid,
                            required: false,
                        },
                        {
                            key: 'dateReminder1',
                            type: 'date',
                            title: this.$t('invoice.field.date_reminder_1').toString(),
                            value: this.invoice.dateReminder1,
                            required: false,
                        },
                        {
                            key: 'dateReminder2',
                            type: 'date',
                            title: this.$t('invoice.field.date_reminder_2').toString(),
                            value: this.invoice.dateReminder2,
                            required: false,
                        },
                        {
                            key: 'dateCreditNote',
                            type: 'date',
                            title: this.$t('invoice.field.date_credit_note').toString(),
                            value: this.invoice.dateCreditNote,
                            required: false,
                        },
                    ],
                    required: true,
                },
            ],
            stepper: true,
        };

        this.form = Object.assign({}, form);
    }

    /**
     * Generates the invoice from the form
     */

    private generateInvoiceFromForm(form: FormInterface): Invoice
    {
        if (!this.invoice)
        {
            return new Invoice(new InvoiceDto());
        }

        const invoice = new Invoice(cloneDeep(this.invoice.dto));

        this.addFieldValues(invoice, form.items);

        return invoice;
    }

    /**
     * Adds field values from form items to the passed invoice
     *
     * @param invoice
     * @param items
     */

    private addFieldValues(invoice: Invoice, items: (FormFieldSetInterface | FormFieldInterface)[])
    {
        for (const item of items)
        {
            if (!('key' in item))
            {
                this.addFieldValues(invoice, item.fields);
                continue;
            }

            switch (item.key)
            {
            case 'techspec':
                invoice.techspecId = item.value as number;
                break;

            case 'service':
                invoice.serviceId = item.value as number;
                break;

            case 'domain':
                invoice.domainId = item.value as number;
                break;

            case 'jobCode':
                invoice.jobCodeId = item.value as number;
                break;

            case 'invoiceNumber':
                invoice.invoiceNumber = item.value as string;
                break;

            case 'title':
                invoice.title = item.value as string;
                break;

            case 'paymentTerms':
                invoice.paymentTerms = item.value as string;
                break;

            case 'notes':
                invoice.notes = item.value as string;
                break;

            case 'amount':
                invoice.amount = new Decimal(item.value as string);
                break;

            case 'adwordsAmount':
                invoice.adwordsAmount = new Decimal(item.value as string);
                break;

            case 'miscAmount':
                invoice.miscAmount = new Decimal(item.value as string);
                break;

            case 'dateRequested':
                invoice.dateRequested = item.value as Date;
                break;

            case 'dateSent':
                invoice.dateSent = item.value as Date;
                break;

            case 'datePaid':
                invoice.datePaid = item.value as Date;
                break;

            case 'dateReminder1':
                invoice.dateReminder1 = item.value as Date;
                break;

            case 'dateReminder2':
                invoice.dateReminder2 = item.value as Date;
                break;

            case 'dateCreditNote':
                invoice.dateCreditNote = item.value as Date;
                break;
            }
        }
    }
}